import React, {useEffect, useState} from 'react';
import {createUseStyles, useTheme} from 'react-jss';
import classNames from 'classnames';
import * as R from 'ramda';
import {useTranslation} from 'react-i18next';

import Header from '@modules/Chain/Header';
import Footer from '@modules/Chain/Footer';

import Container from '@components/Container';
import PreLoading from '@components/PreLoading';

import dcp1Image from '@asserts/images/products/dcp1.png';
import dcp2Image from '@asserts/images/products/dcp2.png';
import dcp3Image from '@asserts/images/products/dcp3.png';
import dcp4Image from '@asserts/images/products/dcp4.png';
import trackinfoImage from '@asserts/images/products/trackinfo.png';
import mapImage from '@asserts/images/products/map.png';

import {trunc} from '@utils/tools';
import {fetch, getBlockBest, getTransactionsInfo} from '@utils/fetch';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Shanghai');

const useStyles = createUseStyles(({
  rem,
}) => ({
  base: {
    width: '100%',
    boxSizing: 'border-box',
    padding: [rem(30), rem(20)],
    color: 'rgba(0, 0, 0, 1)',
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
  root: {
    extend: 'base',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: '#F8F9FA',
    boxShadow: '0px -4px 20px 0px rgba(169, 190, 216, 0.2), 0px 4px 10px 0px rgba(169, 190, 216, 0.24)',
  },
  title: {
    fontSize: rem(24),
    fontWeight: 'bold',
    textAlign: 'center',
  },
  subTitle: {
    fontSize: rem(20),
    color: 'rgba(89, 89, 89, 1)',
    margin: [rem(16), 0],
  },
  tracItem: {
    boxSizing: 'border-box',
    position: 'relative',
    height: 120,
    width: '100%',
    borderRadius: 5,
    marginBottom: 15,
    color: '#000000',
    letterSpacing: 1,
    padding: [rem(10), rem(4)],
    '&::after': {
      position: 'absolute',
      content: '""',
      height: rem(80),
      width: 1,
      top: rem(100),
      left: rem(50),
      backgroundColor: 'rgba(231, 245, 254, 1)',
      border: '2px solid rgba(231, 245, 254, 1)',
    },
    '&:last-child::after': {
      border: 0,
      backgroundColor: '#FFF',
    }
  },
  itemImage: {
    zIndex: 9,
    height: rem(100),
  },
  itemCenter: {
    color: 'rgba(0, 0, 0, 1)',
    paddingLeft: rem(20),
    minWidth: rem(220),
    maxWidth: rem(220),
    '& > span': {
      lineHeight: '20px',
      fontSize: rem(10),
    },
    '& > span:nth-child(1)': {
      lineHeight: '30px',
      fontSize: rem(16),
    },
    '& > span:nth-child(2)': {
      color: 'rgba(87, 188, 249, 1)',
      fontSize: rem(10),
    },
    '& > span:nth-child(3)': {
      fontSize: rem(12),
    },
    '& > span:nth-child(4)': {
      color: 'rgba(0, 0, 0, 0.4)',
    },
  },
  tracTitle: {
    fontSize: rem(16),
    fontWeight: 'bold',
    lineHeight: '20px !important',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    wordBreak: 'break-all',
  },
  comfirm: {
    fontSize: rem(12),
    color: 'rgba(140, 140, 140, 1)',
    lineHeight: '20px !important',
  },
  content: {
    extend: 'base',
    textAlign: 'center',
    marginTop: rem(20),
  },
  conTitle: {
    fontSize: rem(24),
    fontWeight: 'bold',
    lineHeight: rem(30),
    color: 'rgba(38, 38, 38, 1)',
    '& > span': {
      color: 'rgba(87, 188, 249, 1)',
      marginRight: 10
    },
    '& > p': {
      margin: [rem(10), 0],
    }
  },
  conSub: {
    fontSize: rem(13),
    maxWidth: rem(320),
    lineHeight: rem(20),
    margin: [rem(20), 0],
    color: 'rgba(89, 89, 89, 1)',
  },
}), {
  name: 'ChainPage'
})

// 区块链记录
const ChainPage = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();

  const [locale, setLocale] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const [chainData, setChainData] = useState([]);

  let TXID = [];
  const mapBlockInfo = (arr, id) => {
    arr.forEach(item => {
      const txId = R.pathOr([], ['blockinfo', 'txId'])(item);
      TXID.push({
        id,
        txId,
      });
    });
  }

  // 获取溯源记录中所有txId
  const getTxId = data => {
    const dcp1Info = R.pathOr([], ['jsonData', 'dcp1'])(data);
    const dcp2Info = R.pathOr([], ['jsonData', 'dcp2'])(data);
    const dcp3Info = R.pathOr([], ['jsonData', 'dcp3'])(data);
    const dcp4Info = R.pathOr([], ['jsonData', 'dcp4'])(data);
    const trackinfo = R.pathOr([], ['jsonData', 'trackinfo'])(data);

    mapBlockInfo(dcp1Info, 'dcp1');
    mapBlockInfo(dcp2Info, 'dcp2');
    mapBlockInfo(dcp3Info, 'dcp3');
    mapBlockInfo(dcp4Info, 'dcp4');
    mapBlockInfo(trackinfo, 'trackinfo');
    return TXID;
  }

  const getChainName = id => {
    switch(String(id)) {
      case 'dcp1':
        return t('procurement');
      case 'dcp2':
        return t('yarn');
      case 'dcp3':
        return t('weav');
      case 'dcp4':
        return t('garment');
      case 'trackinfo':
          return t('shipment');
      default:
        return t('procurement');
    }
  }

  const getChainIcon = id => {
    switch(String(id)) {
      case 'dcp1':
        return dcp1Image;
      case 'dcp2':
        return dcp2Image;
      case 'dcp3':
        return dcp3Image;
      case 'dcp4':
        return dcp4Image;
      case 'trackinfo':
          return trackinfoImage;
      default:
        return dcp1Image;
    }
  }

  // 解析所有区块数据，并组装成最终数据
  const analysisTxInfo = async (arr, blockBest) => {
    const chainArr = [];
    const getBlockNumber = (txId, chain) => new Promise((resolve) => {
       getTransactionsInfo(txId).then(res => {
         const currBlockNum = R.pathOr(0, ['data', 'meta', 'blockNumber'])(res);
         const timestamp = R.pathOr(new Date().getTime(), ['data', 'meta', 'blockTimestamp'])(res);
         chain.block = currBlockNum;
         chain.number = blockBest - currBlockNum;  // 最新区块 - 当前区块 = 在几个区块中确认
         chain.time = timestamp;
         resolve();
       });
    });

    for (let i = 0; i < arr.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const curr = arr[i];
      const txId = R.pathOr(0, ['txId'])(curr);
      const chain = {
        txId,
        id: curr.id,
        name: getChainName(curr.id),
        icon: getChainIcon(curr.id),
      };
      await getBlockNumber(txId, chain);
      chainArr.push(chain);
    }
    return chainArr;
  }

  const fetchData = language => {
    setLoading(true);
    const params = {
      types: [
        'customizedDisplayInfo',
        'trackinfo',
        'dcp1',
        'dcp2',
        'dcp3',
        'dcp4'
      ],
      language,
    };
    fetch(params).then((data) => {
      setData(data);
      const txArr = getTxId(data);
      getBlockBest().then(res => {
        const number = R.pathOr(0, ['data', 'number'])(res);
        analysisTxInfo(txArr, number).then(res => {
          setChainData(res);
          setLoading(false);
        });
      });
    }).catch((err) => {
      console.log(err);
    });
  }

  useEffect(() => {
    fetchData(locale);
    return () => {
      setData(null);
      setChainData([]);
    }
  }, [locale]);

  const createMain = () => {
    return (
      <>
        <Header setLocale={setLocale} />
        <Container className={classNames(classes.root)}>
          <span className={classes.title}>{t('block_rds')}</span>
          <span className={classes.subTitle}>VeChain Thor {t('blockchain')}</span>

          <Container
            flex
            direction='column'
          >
            {
              (chainData && chainData.length !== 0) ? (
                chainData.map((item, index) => (
                  <Container
                    flex
                    alignItems='center'
                    justify='space-around'
                    key={String(index)}
                    className={classNames(classes.tracItem)}
                  >
                    <img src={item.icon} alt='trac' className={classes.itemImage} />
                    <Container
                      flex
                      direction='column'
                      className={classes.itemCenter}
                    >
                      <span className={classes.tracTitle}>{item.name}</span>
                      <span>{`#${item.block}`}</span>
                      <span>{trunc(item.txId, 8, 10)}</span>
                      <span>{dayjs.tz(item.time).format('YYYY-MM-DD HH:mm:ss')} UTC</span>
                      <span className={classes.comfirm}>
                        {t('Confirmed', {
                          number: item.number,
                        })}
                      </span>
                    </Container>
                  </Container>
                ))
              ) : null
            }
          </Container>
        </Container>
        <Container
          flex
          direction='column'
          alignItems='center'
          className={classes.content}
        >
          <div className={classes.conTitle}>
            <span>VeChain Thor</span>{t('blockchain')}
            <p>{t('node')}</p>
          </div>
          <span className={classes.conSub}>
            {t('introduce')}
          </span>

          <img src={mapImage} alt="logo" width={320} />
        </Container>
        <Footer data={data} t={t} />
      </>
    )
  }

  return (
    loading ? (<PreLoading />) : createMain()
  )
};

export default ChainPage;
